import { Link } from "react-router-dom"
import Button from "components/button"
import { EyeIcon, PencilIcon } from "@heroicons/react/outline"
import {
  formatNumber,
  getEventAddonQuantityModeLabel,
  getEventAddonScopeLabel,
  getEventAddonTypeLabel,
  isHaveActionPermission,
} from "utills/helpers"
import { Tag } from "components/tag"
import { capitalize } from "lodash"
import { HistoryLogButton } from "components/historyLogButton"

interface IEventAddon {
  id: string
  name_en: string
  name_vi: string
  desc_en: string
  desc_vi: string
  base_price: number
  stock_total: number | null
  stock_remaining: number | null
  status: string
  type: string
  scope: string
  position: number
  required: boolean
  is_sold_out: boolean
  qty_mode: string
  qty_min: number
  qty_max: number
  has_note: boolean
  note_required: boolean
  low_stock_threshold: number
}

interface EventAddonCardProps {
  data?: IEventAddon[]
  pageSize: number
  currentPage: number
  _toggleSoldOut: (id: string, isSoldOut: boolean) => void
}

export const EventAddonCard = ({
  data = [],
  pageSize = 10,
  currentPage = 1,
  _toggleSoldOut,
}: EventAddonCardProps) => {
  return (
    <div className="space-y-4">
      {data.map((item: IEventAddon, index: number) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">{item.name_vi}</h3>
              <p className="text-sm text-gray-600 mt-1">{item.name_en}</p>
            </div>
            <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {getEventAddonTypeLabel(item.type)}
            </span>
          </div>

          <div className="space-y-2 mb-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Price:</span>
              <span className="font-medium">{formatNumber(item.base_price)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Stock:</span>
              <span className={`font-medium ${item.stock_total !== null && item.stock_total <= item.low_stock_threshold ? 'text-red-600' : 'text-green-600'}`}>
                {item.stock_total ?? "Unlimited"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Stock Remaining:</span>
              <span className={`font-medium ${item.stock_remaining !== null && item.stock_remaining <= item.low_stock_threshold ? 'text-red-600' : 'text-green-600'}`}>
                {item.stock_remaining ?? "Unlimited"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Scope:</span>
              <span className="font-medium">
                {getEventAddonScopeLabel(item.scope)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Quantity Mode:</span>
              <span className="font-medium">
                {getEventAddonQuantityModeLabel(item.qty_mode)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Status:</span>
              <Tag text={capitalize(item.status)} type={item.status === "active" ? "green" : "pink"} />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Sold Out:</span>
              <Tag
                text={item.is_sold_out ? "Yes" : "No"}
                type={item.is_sold_out ? "pink" : "green"}
              />
            </div>
          </div>

          <div className="flex gap-2 pt-3 border-t border-gray-200">
            <HistoryLogButton
              className="flex-1 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300 text-xs border-none !text-gray-900"
              resource={`ws-event-addons/${item.id}/history`}
              showTitleOnMobile
            />
            <Link to={`/events/addons/${item.id}`} className="flex-1">
              <Button className="w-full h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300 text-xs !text-gray-900">
                <EyeIcon className="w-4 h-4 mr-1 text-inherit" />
                View
              </Button>
            </Link>
            {isHaveActionPermission("addons", "events", "edit") ? (
              <Link to={`/events/addons/${item.id}/edit`} className="flex-1">
                <Button className="w-full h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300 text-xs !text-gray-900">
                  <PencilIcon className="w-4 h-4 mr-1 text-inherit" />
                  Edit
                </Button>
              </Link>
            ) : null}
            {isHaveActionPermission("addons", "events", "edit") ? (
              <Button
                className={`flex-1 h-8 text-xs ${
                  item.is_sold_out
                    ? "bg-green-100 hover:bg-green-200 active:bg-green-300 !text-green-700"
                    : "bg-red-100 hover:bg-red-200 active:bg-red-300 !text-red-600"
                }`}
                onClick={() => _toggleSoldOut(item.id, item.is_sold_out)}
              >
                {item.is_sold_out ? "Available" : "Sold Out"}
              </Button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  )
}
